import React, { useEffect, useRef } from 'react';
import { 
  FaGavel, FaFileContract, FaMoneyBillWave, FaUserClock, 
  FaShieldAlt, FaExclamationTriangle, FaPhoneAlt, FaEnvelope, 
  FaMapMarkerAlt, FaCheckCircle, FaClock, FaTools, FaCar 
} from 'react-icons/fa';

const TermsConditions = () => {
  const headerRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === headerRef.current) {
              entry.target.classList.add('animate-fade-down');
            }
            if (entry.target === contentRef.current) {
              entry.target.classList.add('animate-fade-up');
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    if (contentRef.current) observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br from-gray-50 via-white to-red-50 py-16 md:py-24">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-72 h-72 bg-red-400 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-red-300 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-red-200 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10 max-w-5xl">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="text-center mb-12 opacity-0 transition-all duration-700"
          style={{ transform: 'translateY(-20px)' }}
        >
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-600 px-4 py-1.5 rounded-full mb-6">
            <FaGavel className="text-sm" />
            <span className="text-xs font-semibold uppercase tracking-wider">Legal Agreement</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-gray-800">Terms &amp; </span>
            <span className="text-red-600 relative inline-block">
              Conditions
              <span className="absolute bottom-0 left-0 w-full h-1 bg-red-500 rounded-full"></span>
            </span>
          </h1>
          <div className="w-24 h-1 bg-red-500 rounded-full mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Last Updated: January 1, 2025
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Ambris Bike & Car Service Center | Sathawa, Sarnath, Varanasi - 221007
          </p>
        </div>

        {/* Main Content */}
        <div
          ref={contentRef}
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 md:p-10 border border-red-200 shadow-xl opacity-0 transition-all duration-700"
          style={{ transform: 'translateY(30px)' }}
        >
          <div className="prose prose-gray max-w-none">
            {/* Contact Info Card */}
            <div className="mb-8 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-5 border border-red-200">
              <h3 className="text-red-600 font-semibold mb-3 flex items-center gap-2">
                <FaFileContract className="text-sm" />
                Business Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <FaMapMarkerAlt className="text-red-500 text-lg" />
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="text-sm text-gray-800 font-medium">Sathawa, Sarnath, Varanasi,<br />Uttar Pradesh - 221007</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FaPhoneAlt className="text-red-500 text-lg" />
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="text-sm text-gray-800 font-medium">+91 8081321406</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FaEnvelope className="text-red-500 text-lg" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm text-gray-800 font-medium">info@ambrisservice.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Introduction / Acceptance */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaCheckCircle className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Acceptance of Terms</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using the website of <strong>Ambris Bike & Car Service Center</strong> ("we," "our," "us"), 
                booking our services, or visiting our physical location at <strong>Sathawa, Sarnath, Varanasi - 221007</strong>, 
                you agree to be bound by these Terms &amp; Conditions. If you do not agree with any part of these terms, 
                please do not use our services or website.
              </p>
            </div>

            {/* Services Offered */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaTools className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Services Offered</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3">
                Ambris Bike & Car Service Center provides the following services:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Bike Servicing:</strong> Regular maintenance, oil changes, engine repair, brake service, and more.</li>
                <li><strong>Car Servicing:</strong> Comprehensive car maintenance, engine diagnostics, AC service, and repairs.</li>
                <li><strong>Repair Works:</strong> Mechanical and electrical repairs for both bikes and cars.</li>
                <li><strong>Quick Checkups:</strong> Express inspection and diagnostic services.</li>
                <li><strong>Genuine Parts Replacement:</strong> Use of authentic spare parts (subject to availability).</li>
              </ul>
              <p className="text-gray-600 text-sm mt-3 italic">
                Note: Specific services may vary based on vehicle make, model, and condition. Please contact us for detailed information.
              </p>
            </div>

            {/* Appointment & Service Booking */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaUserClock className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Appointment &amp; Service Booking</h2>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Appointments can be made via phone at <strong>+91 8081321406</strong>, email at <strong>info@ambrisservice.com</strong>, or by visiting our center.</li>
                <li>We recommend booking in advance to ensure timely service.</li>
                <li>Cancellations or rescheduling must be communicated at least <strong>2 hours</strong> before the scheduled appointment time.</li>
                <li>Repeated no-shows may result in service restrictions.</li>
                <li>Walk-in customers are welcome but may experience longer wait times.</li>
              </ul>
            </div>

            {/* Pricing & Payment */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaMoneyBillWave className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Pricing &amp; Payment</h2>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>All service charges are quoted in <strong>Indian Rupees (INR)</strong>.</li>
                <li>Prices are subject to change without prior notice. However, quoted prices for confirmed bookings will be honored.</li>
                <li>Payment can be made via <strong>Cash, UPI, Credit/Debit Cards, or Bank Transfer</strong>.</li>
                <li>A <strong>service estimate</strong> will be provided before any major repair work begins.</li>
                <li>Additional charges may apply if unforeseen issues are discovered during service (customer will be informed before proceeding).</li>
                <li><strong>GST</strong> and other applicable taxes will be added as per government regulations.</li>
              </ul>
            </div>

            {/* Customer Responsibilities */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaCar className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Customer Responsibilities</h2>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide accurate information about your vehicle's make, model, year, and existing issues.</li>
                <li>Remove all personal belongings from the vehicle before service.</li>
                <li>Inform us of any known pre-existing conditions or modifications to the vehicle.</li>
                <li>Collect the vehicle promptly after service completion notification.</li>
                <li>Provide valid identification when required for service or payment.</li>
              </ul>
            </div>

            {/* Warranty & Guarantee */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaShieldAlt className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Warranty &amp; Guarantee</h2>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Service Warranty:</strong> Our workmanship is warranted for <strong>30 days</strong> or <strong>1,000 kilometers</strong> (whichever comes first) from the date of service.</li>
                <li><strong>Parts Warranty:</strong> Genuine parts come with manufacturer warranty. Aftermarket parts warranty varies by product.</li>
                <li>The warranty covers defects in workmanship but <strong>does not cover</strong>:
                  <ul className="list-circle pl-6 mt-1">
                    <li>Normal wear and tear</li>
                    <li>Damage caused by accidents, misuse, or neglect</li>
                    <li>Modifications or repairs done by third parties</li>
                    <li>Failure to follow maintenance recommendations</li>
                  </ul>
                </li>
                <li>Warranty claims must be made at our Sarnath, Varanasi location with original service invoice.</li>
              </ul>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaExclamationTriangle className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Limitation of Liability</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-3">
                To the maximum extent permitted by law, Ambris Bike & Car Service Center shall not be liable for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Any indirect, incidental, special, consequential, or punitive damages.</li>
                <li>Loss of profits, revenue, data, or use of the vehicle.</li>
                <li>Delays caused by parts unavailability, labor disputes, or acts of God.</li>
                <li>Issues arising from pre-existing conditions not disclosed by the customer.</li>
                <li>Damage caused by third-party services performed after our service.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim.
              </p>
            </div>

            {/* Vehicle Pickup & Delivery */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <div className="flex items-center gap-3 mb-4">
                <FaClock className="text-red-500 text-2xl" />
                <h2 className="text-2xl font-bold text-gray-800 m-0">Vehicle Pickup &amp; Delivery</h2>
              </div>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Vehicle pickup and delivery services may be available on request (subject to availability).</li>
                <li>Additional charges may apply for transportation services.</li>
                <li>The customer bears responsibility for vehicle security during transit if using third-party transport services.</li>
                <li>Vehicles left at our center beyond <strong>7 days</strong> after service completion notification may incur storage charges.</li>
              </ul>
            </div>

            {/* Right to Refuse Service */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Right to Refuse Service</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to refuse service to any customer or vehicle for any lawful reason, including but not limited to:
                unsafe vehicle conditions, aggressive behavior, fraudulent information, or non-payment of previous services.
              </p>
            </div>

            {/* Intellectual Property */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All content on this website (including text, graphics, logos, and images) is the property of 
                Ambris Bike & Car Service Center and is protected by Indian copyright laws. You may not reproduce, 
                distribute, or modify any content without our prior written consent.
              </p>
            </div>

            {/* Termination */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                We may terminate or suspend your access to our services immediately, without prior notice, for conduct 
                that violates these Terms &amp; Conditions or is harmful to our business interests. Upon termination, 
                your right to use our services ceases immediately.
              </p>
            </div>

            {/* Governing Law */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Governing Law &amp; Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                These Terms &amp; Conditions shall be governed by and construed in accordance with the laws of 
                <strong> India</strong>.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Jurisdiction:</strong> Any disputes arising from these terms shall be subject to the exclusive jurisdiction of courts in <strong>Varanasi, Uttar Pradesh</strong>.</li>
                <li><strong>Dispute Resolution:</strong> In case of any dispute, we encourage amicable resolution through discussion. If unresolved, legal remedies may be pursued as per Indian law.</li>
                <li><strong>Consumer Protection:</strong> Customer rights under the Consumer Protection Act, 2019 remain unaffected.</li>
              </ul>
            </div>

            {/* Amendments */}
            <div className="mb-8 pb-6 border-b border-red-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Amendments to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms &amp; Conditions at any time. Changes will be effective immediately 
                upon posting on this page. Your continued use of our services after any changes constitutes acceptance of 
                the new terms. Please review this page periodically for updates.
              </p>
            </div>

            {/* Contact Information */}
            <div className="mt-6 bg-red-50 rounded-xl p-6 border border-red-200">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <FaEnvelope className="text-red-500" />
                Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding these Terms &amp; Conditions, 
                please contact us at:
              </p>
              <div className="space-y-2">
                <p className="flex items-center gap-3 text-gray-700">
                  <FaMapMarkerAlt className="text-red-500" />
                  <strong>Address:</strong> Sathawa, Sarnath, Varanasi, Uttar Pradesh - 221007
                </p>
                <p className="flex items-center gap-3 text-gray-700">
                  <FaPhoneAlt className="text-red-500" />
                  <strong>Phone:</strong> +91 8081321406
                </p>
                <p className="flex items-center gap-3 text-gray-700">
                  <FaEnvelope className="text-red-500" />
                  <strong>Email:</strong> info@ambrisservice.com
                </p>
              </div>
              <p className="text-gray-600 text-sm mt-4 pt-3 border-t border-red-200">
                <strong>Business Hours:</strong> Monday - Saturday | 9:00 AM - 7:00 PM<br />
                <strong>Sunday:</strong> Closed (Emergency services available on call)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeDown {
          0% {
            opacity: 0;
            transform: translateY(-20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-down {
          animation: fadeDown 0.8s ease-out forwards !important;
        }
        .animate-fade-up {
          animation: fadeUp 0.8s ease-out forwards !important;
        }
      `}</style>
    </section>
  );
};

export default TermsConditions;